// src/components/CommentSection.js
import React, { useState, useEffect, useRef, memo } from 'react';
import useAuth from '../../../hooks/useAuth';
import { getComments, createComment, toggleCommentLike, deleteComment } from '../../../services/commentService';
import { formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { FaHeart, FaReply, FaTrash, FaUser } from 'react-icons/fa';

// Memoized Comment component to prevent unnecessary re-renders
const Comment = memo(({ comment, onReply, onDelete, onLike, replyTo, user, isAuthenticated, onSubmitReply, loading, depth = 0 }) => {
  const replyInputRef = useRef(null);
  const [replyText, setReplyText] = useState('');
  const [guestInfo, setGuestInfo] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });

  // Focus the reply input when it appears
  useEffect(() => {
    if (replyTo === comment._id && replyInputRef.current) {
      replyInputRef.current.focus();
    }
  }, [replyTo, comment._id]);

  // Only allow replies on top-level comments (depth 0)
  const canAddReply = depth === 0;
  
  // Check if user is admin
  const isAdmin = user && (user.role === 'admin' || user.role === 'super-admin');

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'Date unavailable';
      
      const date = parseISO(dateString);
      if (!isValid(date)) return 'Invalid date';
      
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date unavailable';
    }
  };

  const handleSubmitReply = (e) => {
    e.preventDefault();
    // Always pass the current comment's ID as parent, regardless of depth
    onSubmitReply(replyText, guestInfo, depth === 0 ? comment._id : comment.parentComment);
    setReplyText('');
    setGuestInfo({ firstName: '', lastName: '', email: '' });
  };

  const handleGuestInfoChange = (e) => {
    const { name, value } = e.target;
    setGuestInfo(prev => ({ ...prev, [name]: value }));
  };

  const getDisplayName = (author) => {
    if (!author) return 'Anonymous';
    
    // If author is just an ID string
    if (typeof author === 'string') return 'User';
    
    // Handle guest authors
    if (comment.isGuestComment && comment.guestAuthor) {
      return `${comment.guestAuthor.firstName} ${comment.guestAuthor.lastName}`;
    }
    
    // Try to construct full name first
    if (author.firstName && author.lastName) {
      return `${author.firstName} ${author.lastName}`;
    }
    
    // Then try individual fields
    return author.username || 
           author.firstName || 
           author.lastName || 
           author.email?.split('@')[0] || 
           'Anonymous';
  };

  const getAuthorAvatar = (author) => {
    // For guest comments, return null to use the default avatar
    if (comment.isGuestComment) {
      return null;
    }
    
    if (!author || typeof author === 'string') {
      return null;
    }
    return author.avatar || author.profilePicture || null;
  };

  const displayAuthorName = () => {
    if (comment.isGuestComment && comment.guestAuthor) {
      return (
        <>
          {`${comment.guestAuthor.firstName} ${comment.guestAuthor.lastName}`}
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(Guest)</span>
        </>
      );
    } else if (comment.author) {
      return (
        <>
          {getDisplayName(comment.author)}
          {comment.author.role && (
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({comment.author.role})</span>
          )}
        </>
      );
    } else {
      return 'Anonymous';
    }
  };
  
  // Check if user can delete this comment
  const canDelete = 
    // Admin can delete any comment
    isAdmin || 
    // User can delete their own comments
    (user && comment.author && comment.author._id === user._id) || 
    // Guest can delete their own comments
    (comment.isGuestComment && (() => {
      const guestCommentIds = localStorage.getItem('guestCommentIds');
      return guestCommentIds && JSON.parse(guestCommentIds).includes(comment._id);
    })());

  return (
    <div className={depth > 0 ? 'pl-8' : ''}>
      <div className={`bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm mb-4 ${depth > 0 ? 'border-l-4 border-blue-100 dark:border-blue-900' : ''}`}>
        <div className="flex justify-between items-start">
          <div className="flex items-center space-x-3">
            {getAuthorAvatar(comment.author) ? (
              <img 
                src={getAuthorAvatar(comment.author)} 
                alt={getDisplayName(comment.author)} 
                className="w-10 h-10 rounded-full"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                <FaUser className="text-gray-500 dark:text-gray-400" />
              </div>
            )}
            <div className="flex flex-col">
              <h4 className="font-semibold text-blue-600 dark:text-blue-400">
                {displayAuthorName()}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatDate(comment.createdAt)}
              </p>
            </div>
          </div>
          {/* Display delete button if user can delete this comment */}
          {canDelete && (
            <div className="flex space-x-2">
              <button
                onClick={() => onDelete(comment._id, isAdmin)}
                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                title={isAdmin ? "Delete as admin" : "Delete comment"}
              >
                <FaTrash />
                {isAdmin && !((user && comment.author && comment.author._id === user._id)) && (
                  <span className="text-xs ml-1">(Admin)</span>
                )}
              </button>
            </div>
          )}
        </div>
        
        <div className="mt-2">
          {depth > 0 && comment.parentComment && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Replying to <span className="font-medium text-blue-600 dark:text-blue-400">
                {comment.isParentGuestComment && comment.parentGuestAuthor 
                  ? `${comment.parentGuestAuthor.firstName} ${comment.parentGuestAuthor.lastName}`
                  : (comment.parentAuthor ? getDisplayName(comment.parentAuthor) : 'Anonymous')}
              </span>
            </p>
          )}
          <p className="text-gray-700 dark:text-gray-300">{comment.content}</p>
        </div>
        
        <div className="mt-3 flex items-center space-x-4">
          <button
            onClick={() => onLike(comment._id)}
            className={`flex items-center space-x-1 ${
              comment.likes && comment.likes.includes(user?._id) 
                ? 'text-red-500 dark:text-red-400' 
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            <FaHeart />
            <span>{comment.likes?.length || 0}</span>
          </button>
          
          {/* Only show reply button for top-level comments (depth 0) */}
          {canAddReply && (
            <button
              onClick={() => onReply(replyTo === comment._id ? null : comment._id)}
              className="flex items-center space-x-1 text-gray-500 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-300"
            >
              <FaReply />
              <span>{replyTo === comment._id ? 'Cancel' : 'Reply'}</span>
            </button>
          )}
        </div>

        {/* Reply Form - only show for top-level comments */}
        {canAddReply && replyTo === comment._id && (
          <form onSubmit={handleSubmitReply} className="mt-4 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              Replying to <span className="font-medium text-blue-600 dark:text-blue-400">
                {comment.isGuestComment && comment.guestAuthor 
                  ? `${comment.guestAuthor.firstName} ${comment.guestAuthor.lastName}`
                  : getDisplayName(comment.author)}
              </span>
            </p>
            <textarea
              ref={replyInputRef}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
              placeholder={`Reply to ${
                comment.isGuestComment && comment.guestAuthor 
                  ? `${comment.guestAuthor.firstName} ${comment.guestAuthor.lastName}`
                  : getDisplayName(comment.author)
              }...`}
              rows="3"
            />

            {!isAuthenticated && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <div>
                  <label htmlFor="reply-firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Name*
                  </label>
                  <input
                    type="text"
                    id="reply-firstName"
                    name="firstName"
                    value={guestInfo.firstName}
                    onChange={handleGuestInfoChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="reply-lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last Name*
                  </label>
                  <input
                    type="text"
                    id="reply-lastName"
                    name="lastName"
                    value={guestInfo.lastName}
                    onChange={handleGuestInfoChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="reply-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email*
                  </label>
                  <input
                    type="email"
                    id="reply-email"
                    name="email"
                    value={guestInfo.email}
                    onChange={handleGuestInfoChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                    required
                  />
                </div>
              </div>
            )}

            <div className="flex justify-between items-center mt-3">
              {isAuthenticated ? (
                <div className="flex items-center space-x-2">
                  {user?.avatar || user?.profilePicture ? (
                    <img 
                      src={user.avatar || user.profilePicture} 
                      alt={getDisplayName(user)} 
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                      <FaUser className="text-gray-500 dark:text-gray-400" size={12} />
                    </div>
                  )}
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Posting as <span className="font-medium text-blue-600 dark:text-blue-400">{getDisplayName(user)}</span>
                  </span>
                </div>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Fields with * are required
                </span>
              )}
              <button
                type="submit"
                disabled={loading || !replyText.trim() || (!isAuthenticated && (!guestInfo.firstName.trim() || !guestInfo.lastName.trim() || !guestInfo.email.trim()))}
                className={`px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 ${
                  loading || !replyText.trim() || (!isAuthenticated && (!guestInfo.firstName.trim() || !guestInfo.lastName.trim() || !guestInfo.email.trim())) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Posting...' : 'Post Reply'}
              </button>
            </div>
          </form>
        )}

        {/* Display replies only for parent comments (depth 0) */}
        {depth === 0 && comment.replies && comment.replies.length > 0 && (
          <div className="mt-6 space-y-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Replies ({comment.replies.length})
            </h4>
            {/* Sort replies in descending order (newest first) */}
            {[...comment.replies]
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .map(reply => (
                <Comment
                  key={reply._id}
                  comment={reply}
                  onReply={onReply}
                  onDelete={onDelete}
                  onLike={onLike}
                  replyTo={replyTo}
                  user={user}
                  isAuthenticated={isAuthenticated}
                  onSubmitReply={onSubmitReply}
                  loading={loading}
                  depth={1} // Always set depth to 1 for replies
                />
              ))
            }
          </div>
        )}
      </div>
    </div>
  );
});

const CommentSection = ({ blogId }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [guestInfo, setGuestInfo] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [replyTo, setReplyTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { user, isAuthenticated } = useAuth();

  // Clear messages after 5 seconds
  useEffect(() => {
    if (errorMessage || successMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, successMessage]);

  const handleGuestInfoChange = (e) => {
    const { name, value } = e.target;
    setGuestInfo(prev => ({ ...prev, [name]: value }));
  };

  // Fetch comments
  const fetchComments = async () => {
    if (!blogId) {
      setErrorMessage('Unable to load comments: Missing blog ID');
      setComments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getComments(blogId, 'Blog', page);
      console.log('### response', response);
      
      // Get comments from the response and ensure it's an array
      let newComments = [];
      
      if (response) {
        // The response structure might have data.data.comments
        if (response.data && response.data.data && Array.isArray(response.data.data.comments)) {
          console.log('Found comments in response.data.data.comments');
          newComments = response.data.data.comments;
        } 
        // Or it might have data.comments 
        else if (response.data && Array.isArray(response.data.comments)) {
          console.log('Found comments in response.data.comments');
          newComments = response.data.comments;
        }
        // Or the data itself might be the comments array
        else if (response.data && Array.isArray(response.data)) {
          console.log('Found comments in response.data');
          newComments = response.data;
        }
        // Last try - check direct response
        else if (Array.isArray(response)) {
          console.log('Found comments directly in response');
          newComments = response;
        }
      }
      
      console.log('Final extracted comments:', newComments);
      
      // Ensure we have a valid array of comments
      if (!Array.isArray(newComments)) {
        console.error('Comments is not an array after extraction attempts', newComments);
        newComments = [];
      }
      
      if (page === 1) {
        setComments(newComments);
      } else {
        setComments(prev => [...prev, ...newComments]);
      }
      
      // Update hasMore based on pagination info
      const pagination = response?.data?.pagination || 
                         response?.data?.data?.pagination || 
                         { page, pages: 1 };
      
      setHasMore(pagination ? page < pagination.pages : false);
      setErrorMessage(null);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setErrorMessage(err.message || 'Failed to load comments');
      setComments([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [blogId, page]);

  // Added a separate effect to reset the page when the blogId changes
  useEffect(() => {
    if (blogId) {
      setPage(1);
    }
  }, [blogId]);

  // Handle new comment submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !blogId) return;
    
    // For guest users, validate all required fields
    if (!isAuthenticated && (!guestInfo.firstName.trim() || !guestInfo.lastName.trim() || !guestInfo.email.trim())) {
      setErrorMessage('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setErrorMessage(null);
      setSuccessMessage(null);
      
      const commentData = {
        content: newComment,
        parentContent: blogId,
        contentType: 'Blog',
        parentComment: null,
        ...((!isAuthenticated && guestInfo) && {
          guestAuthor: {
            firstName: guestInfo.firstName,
            lastName: guestInfo.lastName,
            email: guestInfo.email
          },
          isGuestComment: true
        })
      };

      const response = await createComment(commentData);
      setNewComment('');
      
      // For guest comments, store the comment ID in localStorage to allow deletion later
      if (!isAuthenticated && response.data && response.data.comment) {
        const commentId = response.data.comment._id;
        const existingIds = localStorage.getItem('guestCommentIds');
        
        if (existingIds) {
          const commentIds = JSON.parse(existingIds);
          if (!commentIds.includes(commentId)) {
            commentIds.push(commentId);
            localStorage.setItem('guestCommentIds', JSON.stringify(commentIds));
          }
        } else {
          localStorage.setItem('guestCommentIds', JSON.stringify([commentId]));
        }
      }
      
      if (!isAuthenticated) {
        setGuestInfo({
          firstName: '',
          lastName: '',
          email: ''
        });
      }
      
      // Reset page to 1 when a new comment is added
      setPage(1);
      
      // Fetch comments after successful submission
      await fetchComments();
      
      setSuccessMessage('Comment posted successfully!');
    } catch (err) {
      setErrorMessage(err.message || 'Failed to post comment');
    } finally {
      setLoading(false);
    }
  };

  // Handle like toggle
  const handleLike = async (commentId) => {
    if (!isAuthenticated) {
      setErrorMessage('Please login to like comments');
      return;
    }

    try {
      await toggleCommentLike(commentId);
      await fetchComments();
    } catch (err) {
      setErrorMessage('Failed to toggle like');
    }
  };

  // Handle comment deletion
  const handleDelete = async (commentId, isAdmin = false) => {
    console.log('### handleDelete', commentId, isAdmin);
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      setLoading(true);
      
      // Check if this is a guest comment that we own
      let isGuestComment = false;
      if (!isAuthenticated && !isAdmin) {
        const guestCommentIds = localStorage.getItem('guestCommentIds');
        if (guestCommentIds) {
          const ids = JSON.parse(guestCommentIds);
          isGuestComment = ids.includes(commentId);
          
          // Remove from localStorage if this is a guest comment
          if (isGuestComment) {
            localStorage.setItem('guestCommentIds', JSON.stringify(ids.filter(id => id !== commentId)));
          }
        }
      }
      
      // The admin parameter allows the backend to know it's an admin delete
      await deleteComment(commentId, isAdmin);
      
      // Fetch the updated comments
      fetchComments();
      
      setSuccessMessage('Comment deleted successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error deleting comment:', error);
      setErrorMessage(error.message || 'Failed to delete comment');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  // Handle reply submission
  const handleSubmitReply = async (replyText, replyGuestInfo = null, parentCommentId = null) => {
    // Ensure we always have a parent comment ID
    const actualParentId = parentCommentId || replyTo;

    if (!replyText.trim() || !blogId || !actualParentId) return;
    
    // For guest users, validate all required fields from replyGuestInfo
    if (!isAuthenticated && replyGuestInfo && (!replyGuestInfo.firstName.trim() || !replyGuestInfo.lastName.trim() || !replyGuestInfo.email.trim())) {
      setErrorMessage('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setErrorMessage(null);
      setSuccessMessage(null);
      
      const commentData = {
        content: replyText,
        parentContent: blogId,
        contentType: 'Blog',
        parentComment: actualParentId, // Always use the top-level parent ID
        ...((!isAuthenticated && replyGuestInfo) && {
          guestAuthor: {
            firstName: replyGuestInfo.firstName,
            lastName: replyGuestInfo.lastName,
            email: replyGuestInfo.email
          },
          isGuestComment: true
        })
      };

      const response = await createComment(commentData);
      
      // For guest comments, store the comment ID in localStorage to allow deletion later
      if (!isAuthenticated && response.data && response.data.comment) {
        const commentId = response.data.comment._id;
        const existingIds = localStorage.getItem('guestCommentIds');
        
        if (existingIds) {
          const commentIds = JSON.parse(existingIds);
          if (!commentIds.includes(commentId)) {
            commentIds.push(commentId);
            localStorage.setItem('guestCommentIds', JSON.stringify(commentIds));
          }
        } else {
          localStorage.setItem('guestCommentIds', JSON.stringify([commentId]));
        }
      }
      
      // Clear reply state
      setReplyTo(null);
      
      // Reset page to 1 when a new reply is added
      setPage(1);
      
      // Fetch fresh comments with the new reply
      await fetchComments();
      
      setSuccessMessage('Reply posted successfully!');
    } catch (err) {
      setErrorMessage(err.message || 'Failed to post reply');
    } finally {
      setLoading(false);
    }
  };

  if (!blogId) {
    return null;
  }

  return (
    <div className="mt-8">
      <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-gray-100">Comments</h2>
      
      {/* Messages Section */}
      <div className="mb-6 space-y-2">
        {errorMessage && (
          <div className="bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 p-3 rounded-md">
            {errorMessage}
          </div>
        )}
        {successMessage && (
          <div className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 p-3 rounded-md">
            {successMessage}
          </div>
        )}
      </div>

      {/* Main Comment Form */}
      <form onSubmit={handleSubmit} className="mb-8">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
          placeholder="Write a comment..."
          rows="4"
        />
        
        {/* Guest Info Fields (only show if not authenticated) */}
        {!isAuthenticated && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First Name*
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={guestInfo.firstName}
                onChange={handleGuestInfoChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                required
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Last Name*
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={guestInfo.lastName}
                onChange={handleGuestInfoChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                required
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email*
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={guestInfo.email}
                onChange={handleGuestInfoChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                required
              />
            </div>
          </div>
        )}
        
        <div className="flex justify-between items-center mt-4">
          {isAuthenticated ? (
            <div className="flex items-center space-x-3">
              {user?.avatar || user?.profilePicture ? (
                <img 
                  src={user.avatar || user.profilePicture} 
                  alt="User" 
                  className="w-10 h-10 rounded-full"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                  <FaUser className="text-gray-500 dark:text-gray-400" />
                </div>
              )}
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Posting as <span className="font-semibold">{user?.firstName || user?.username || 'User'}</span>
              </span>
            </div>
          ) : (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Fields with * are required
            </span>
          )}
          <button
            type="submit"
            disabled={loading || !newComment.trim() || (!isAuthenticated && (!guestInfo.firstName.trim() || !guestInfo.lastName.trim() || !guestInfo.email.trim()))}
            className={`px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 ${
              loading || !newComment.trim() || (!isAuthenticated && (!guestInfo.firstName.trim() || !guestInfo.lastName.trim() || !guestInfo.email.trim())) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {loading ? 'Posting...' : 'Post Comment'}
          </button>
        </div>
      </form>

      {/* Comments List - sort in descending order (newest first) */}
      <div className="space-y-6">
        {[...comments]
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .map(comment => (
            <Comment
              key={comment._id}
              comment={comment}
              onReply={setReplyTo}
              onDelete={handleDelete}
              onLike={handleLike}
              replyTo={replyTo}
              user={user}
              isAuthenticated={isAuthenticated}
              onSubmitReply={handleSubmitReply}
              loading={loading}
              depth={0} // Ensure top-level comments have depth 0
            />
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <button
          onClick={() => setPage(p => p + 1)}
          disabled={loading}
          className={`w-full mt-4 py-2 text-center text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {loading ? 'Loading...' : 'Load More Comments'}
        </button>
      )}

      {!loading && comments.length === 0 && (
        <p className="text-center text-gray-500 dark:text-gray-400">No comments yet. Be the first to comment!</p>
      )}
    </div>
  );
};

export default CommentSection;
